import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, useNavigate, useSearchParams} from "react-router-dom";
import {isAuthRedirectError, requireAuth} from "../../shared/services/auth.service";
import {logError} from "../../shared/services/logger.service";
import {useToolbar} from "../../components/ToolbarContext";
import ClientProfilePage from "./profile/ClientProfilePage";
import {ClientDto} from "./dtos/client.dto";
import {getClients} from "./clients.service";
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {
  AddButton,
  CardSpec,
  ListRow,
  StatusListPage,
  useStatusList,
} from "../../shared/components/list-page/ListPageKit";
import {addressOneLine, displayPhone} from "../../shared/components/list-page/list-format";
import {ListWithRecord, useRecordRoute} from "../../shared/components/record/RecordKit";

/*
 * The card's realistic widths, from the Shippy's Tree Service snapshot (866
 * clients, measured in Chrome with the card's own CSS; 98th percentile, so the
 * longest 2% wrap inside their slot):
 *   name     221px at 18px  "Testing For Status Updates"
 *   phone     99px at 14px  "(208) 631-0182" (every number has this shape)
 *   address  295px at 14px  "3821 East Thousand Springs Court, Nampa, ID"
 * Switch points (cardSwitchPoints): one row from 721px, grouped from 598px.
 */
const CARD_SPEC: CardSpec = {
  title: 221,
  status: 0,
  slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 295},
  ],
};

/**
 * The Clients page, as on Android: the title with Add New Client, the Client
 * Search box, and one card per client (name, phone, address). A client opens
 * as its own page (/clients/12, /clients/new) over the list (SQ-574 rule 2):
 * the client profile (SQ-575).
 */
function ClientListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const record = useRecordRoute('/clients');
  const openRecord = record.open;
  const toolbarContext = useToolbar();
  const {hasPermission} = usePermissions();

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    toolbarContext.initToolbar('ClientListPage', '');
    void fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchClients() {
    setLoading(true);
    setError(null);
    try {
      await requireAuth(navigate);
      setClients(await getClients(navigate));
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error fetching clients', {}, e as Error);
      setError(`Couldn't load clients: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  const rows: ListRow[] = useMemo(() => clients.map(client => ({
    key: client.id,
    title: client.name,
    values: {
      phone: displayPhone(client.phoneNumber),
      address: addressOneLine(client.address, client.city, client.state),
    },
    onClick: () => openRecord(client.id),
  })), [clients, openRecord]);

  const list = useStatusList(rows, []);

  // Links from before records had pages: /clients?clientId=12.
  const legacyId = searchParams.get("clientId");
  if (legacyId) return <Navigate to={`/clients/${legacyId.toLowerCase()}`} replace/>;

  return (
    <ListWithRecord
      list={<StatusListPage
        title="Clients"
        titleButton={hasPermission(PermissionCode.ClientsCreate)
          ? <AddButton label="Add New Client" onClick={() => record.open('NEW')}/>
          : undefined}
        searchLabel="Client Search"
        list={list}
        spec={CARD_SPEC}
        loading={loading}
        error={error}
        onRetry={() => void fetchClients()}
      />}
      record={record.id !== undefined && (
        <ClientProfilePage key={record.id} clientId={record.id === 'NEW' ? undefined : record.id}
                           onClose={record.close} onChanged={() => void fetchClients()}/>
      )}
    />
  );
}

export default ClientListPage;
